import { CANONICAL_MEDICATION_NAMES, normalizeMedicationKey } from '../constants/medications';
import { CORRECTION_ZONE_OPTIONS, canonicalCorrectionZone } from '../constants/correctionZones';

const SECTION_PATTERNS = [
  {
    key: 'summary',
    patterns: [
      /^\s*1[\.\)]\s*краткое\s+содержание\s*:?\s*$/i,
      /^\s*#+\s*краткое\s+содержание\s*$/i,
      /^\s*краткое\s+содержание\s*:?\s*$/i,
    ],
  },
  {
    key: 'theory',
    patterns: [
      /^\s*2[\.\)]\s*препараты?\s*(в\s+)?теори[яи]\s*:?\s*$/i,
      /^\s*#+\s*препараты?\s*(в\s+)?теори/i,
      /^\s*препараты?\s*(в\s+)?теори[яи]\s*:?\s*$/i,
    ],
  },
  {
    key: 'practice',
    patterns: [
      /^\s*3[\.\)]\s*препараты?\s*(в\s+)?практик/i,
      /^\s*#+\s*препараты?\s*(в\s+)?практик/i,
      /^\s*препараты?\s*(в\s+)?практик[еа]\s*:?\s*$/i,
    ],
  },
  {
    key: 'zones',
    patterns: [
      /^\s*4[\.\)]\s*зоны?\s+коррекци/i,
      /^\s*#+\s*зоны?\s+коррекци/i,
      /^\s*зоны?\s+коррекци[ия]\s*:?\s*$/i,
    ],
  },
  {
    key: 'timestamps',
    patterns: [
      /^\s*5[\.\)]\s*(тайм[-\s]?код|хронолог)/i,
      /^\s*#+\s*(тайм[-\s]?код|хронолог)/i,
      /^\s*(тайм[-\s]?коды|хронология(\s+и\s+структура)?)\s*:?\s*$/i,
    ],
  },
];

const matchMedication = (raw, catalog = CANONICAL_MEDICATION_NAMES) => {
  const token = String(raw || '').trim().replace(/^[-•*]\s*/, '');
  if (!token) return null;
  const key = normalizeMedicationKey(token);
  if (!key) return null;

  const exact = catalog.find((name) => normalizeMedicationKey(name) === key);
  if (exact) return exact;

  // BioPro / FBio Light без Novacutan
  const withPrefix = catalog.find((name) => {
    const nk = normalizeMedicationKey(name);
    return nk === `novacutan${key}` || key === nk.replace(/^novacutan/, '');
  });
  if (withPrefix) return withPrefix;

  const partial = catalog.find((name) => {
    const nk = normalizeMedicationKey(name);
    return nk.includes(key) || key.includes(nk);
  });
  return partial || null;
};

const splitListItems = (block) => {
  if (!block) return [];
  const items = [];
  String(block)
    .split(/\n|,|;/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const cleaned = line
        .replace(/^[-•*]\s*/, '')
        .replace(/^\d+[\.\)]\s*/, '')
        .trim();
      if (cleaned && !/^(нет|отсутствует|-|—)$/i.test(cleaned)) {
        items.push(cleaned);
      }
    });
  return items;
};

const detectSection = (line) => {
  for (const section of SECTION_PATTERNS) {
    if (section.patterns.some((re) => re.test(line))) {
      return section.key;
    }
  }
  return null;
};

/**
 * Разбирает ответ DeepSeek/GPT в структурированные поля формы вебинара.
 */
export function parseAiWebinarBrief(text, { medicationCatalog = CANONICAL_MEDICATION_NAMES } = {}) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const buckets = {
    summary: [],
    theory: [],
    practice: [],
    zones: [],
    timestamps: [],
  };
  let current = null;

  lines.forEach((line) => {
    const section = detectSection(line.trim());
    if (section) {
      current = section;
      return;
    }
    if (!current) return;
    buckets[current].push(line);
  });

  const summary = buckets.summary.join('\n').trim();
  const theoryRaw = splitListItems(buckets.theory.join('\n'));
  const practiceRaw = splitListItems(buckets.practice.join('\n'));
  const zonesRaw = splitListItems(buckets.zones.join('\n'));
  const chronologyStructure = buckets.timestamps
    .map((line) => line.trim())
    .filter((line) => /^\d{1,2}:\d{2}(?::\d{2})?\s+\S/.test(line))
    .slice(0, 10)
    .join('\n')
    .trim();

  const theory = [];
  const practice = [];
  const unmatchedMeds = [];

  theoryRaw.forEach((item) => {
    const matched = matchMedication(item, medicationCatalog);
    if (matched) {
      if (!theory.includes(matched)) theory.push(matched);
    } else {
      unmatchedMeds.push(item);
    }
  });
  practiceRaw.forEach((item) => {
    const matched = matchMedication(item, medicationCatalog);
    if (matched) {
      if (!practice.includes(matched)) practice.push(matched);
    } else {
      unmatchedMeds.push(item);
    }
  });

  const zones = [];
  const unmatchedZones = [];
  zonesRaw.forEach((item) => {
    const matched = canonicalCorrectionZone(item);
    if (matched) {
      if (!zones.includes(matched)) zones.push(matched);
    } else {
      // точное совпадение с опцией
      const exact = CORRECTION_ZONE_OPTIONS.find(
        (z) => z.toLowerCase() === item.toLowerCase()
      );
      if (exact && !zones.includes(exact)) zones.push(exact);
      else unmatchedZones.push(item);
    }
  });

  return {
    summary,
    theory,
    practice,
    zones,
    chronologyStructure,
    unmatchedMeds: Array.from(new Set(unmatchedMeds)),
    unmatchedZones: Array.from(new Set(unmatchedZones)),
    hasAny:
      !!summary ||
      theory.length > 0 ||
      practice.length > 0 ||
      zones.length > 0 ||
      !!chronologyStructure,
  };
}

/** Промпт для DeepSeek / GPT — подставьте транскрипцию в конец. */
export const DEEPSEEK_WEBINAR_PROMPT = `Ты — ассистент по разметке эстетических вебинаров бренда Novacutan.
По транскрипции подготовь ответ СТРОГО в структуре ниже. Без вступлений, без пояснений, без markdown-таблиц.
Правила по препаратам:
Используй ТОЛЬКО канонические названия:
Novacutan BTA, Novacutan BioPRO, Novacutan Bright, Novacutan Eye, Novacutan FBio, Novacutan FBio DVS, Novacutan FBio DVS Light, Novacutan FBio DVS Medium, Novacutan FBio DVS Volume, Novacutan FBio Light, Novacutan FBio Medium, Novacutan FBio Volume, Novacutan Gentle, Novacutan Master, Novacutan PLA, Novacutan Prima, Novacutan SBio, Novacutan YBio.
Нельзя писать короткие имена (BioPro, FBio, БТА) — только полные канонические.
Правила по зонам:
Короткие канонические названия (Лоб, Скулы, Периорбитальная зона, Жевательная мышца, Шея, Декольте, Кисти рук, Лицо целиком и т.п.).
Без доз (мл) и без пояснений в скобках.
Формат ответа:
1. Краткое содержание
Ровно 3 предложения по-русски: о чём вебинар, ключевые препараты/методики, практическая ценность.
2. Препараты теория
По одному препарату на строку (только канонические имена) — то, что обсуждали в теории.
Если нет — напиши: Нет
3. Препараты практика
По одному препарату на строку — то, что реально использовали в практике.
Если нет — напиши: Нет
4. Зоны коррекции
По одной зоне на строку.
Если нет — напиши: Нет
5. Тайм-коды
Только крупные смысловые блоки. Обычно 4–8 меток на весь вебинар, максимум 10.
Формат: HH:MM:SS Название блока
Можно отмечать только:
- начало / представление
- теорию целиком (одна метка)
- практику (отдельная метка на каждую явную демонстрацию/пациента)
- вопросы и ответы / завершение
Нельзя дробить на мелочи. Не делай отдельные тайм-коды на:
обзор препарата, свойства, показания, противопоказания, сравнение продуктов, клинические случаи в теории, технику инъекций внутри одного блока, комментарии спикера, рекламные вставки, мелкие подтемы.
Плохо:
00:10:00 Обзор BioPRO
00:18:00 Клинический случай 1
00:25:00 Техника в скулы
Хорошо:
00:00:00 Начало вебинара
00:04:30 Теория
00:38:10 Практика — пациент 1
01:05:20 Практика — пациент 2
01:28:00 Вопросы и ответы
Если точного времени нет — поставь ближайшее по контексту. Не выдумывай десятки меток.

---
ТРАНСКРИПЦИЯ:
`;
